import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "environments/environment";
import { Customer } from "../models/customer.type";
import { SubmittedDossier as SubmittedDossier } from "../models/submittedDossier.type";


@Injectable({
    providedIn: 'root'
})
export class CustomerService
{
    protected apiRoute =  environment.RESOURCE_API;

    public _currentCustomerSubject$ = new BehaviorSubject<Customer>(null);
    private _canSave : ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private _sendAddress$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _relod : ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private _firstNameDeclarantInfo : BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private _lastNameDeclarantInfo : BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private _dossierSubmittedInformation$ = new BehaviorSubject<SubmittedDossier>(null);
    dialogPreviewCustomer: Customer = null;
    private filteredCustomerList = new BehaviorSubject<Customer[]>(null);
    filteredCustomerListData = this.filteredCustomerList.asObservable();
    private isLastProspects = new BehaviorSubject<boolean>(null);
    isLastProspectItems = this.isLastProspects.asObservable();
    private lastCustomerCallTime: number = 0;
    private lastCustomer: Customer | null = null;

    checkIstLastProspects(isLastProspect: any){
        this.isLastProspects.next(isLastProspect);
    }

    updateFilteredCustomerList(customerList: any) {
        this.filteredCustomerList.next(customerList);
    }

    set canSave(value: boolean)
    {
        this._canSave.next(value);
    }

    get canSave$(): Observable<boolean>
    {
        return this._canSave.asObservable();
    }

    set firstNameDeclarantInfo(value: string)
    {
        this._firstNameDeclarantInfo.next(value);
    }

    get firstNameDeclarantInfo$(): Observable<string>
    {
        return this._firstNameDeclarantInfo.asObservable();
    }

    set lastNameDeclarantInfo(value: string)
    {
        this._lastNameDeclarantInfo.next(value);
    }

    get lastNameDeclarantInfo$(): Observable<string>
    {
        return this._lastNameDeclarantInfo.asObservable();
    }

    set dossierSubmittedInformation(value: SubmittedDossier)
    {
        this._dossierSubmittedInformation$.next(value);
    }

    get dossierSubmittedInformation$(): Observable<SubmittedDossier>
    {
        return this._dossierSubmittedInformation$.asObservable();
    }

    set reload(value: boolean)
    {
        this._relod.next(value);
    }

    get reload$(): Observable<boolean>
    {
        return this._relod.asObservable();
    }

    constructor(private _http: HttpClient) {}

    public getCurrentCustomer() {
        return this._currentCustomerSubject$.getValue();
    }

    public setCurrentCustomer(customer: Customer) {
        this._currentCustomerSubject$.next(customer);
    }

    getCustomerById(customerId: string): Observable<Customer> {
        const now = Date.now();
        if (now - this.lastCustomerCallTime < 3_000 && this.lastCustomer) {
            console.warn('API call to getCustomerById blocked: returning cached customer.');
            return of(this.lastCustomer);
        }

        this.lastCustomerCallTime = now;

        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.getById + customerId;
        return this._http.get(route, this.prepareHeader()).pipe(
            map(res => res as Customer),
            tap(customer => {
                this.lastCustomer = customer; // cache it
            })
        );
    }
    
    getCustomers(companyId: string): Observable<Customer[]> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.getAllActive.replace('companyId', companyId);
        return this._http.get<Customer[]>(route, this.prepareHeader());
    }

    updateCustomer(customer : Customer): Observable<Customer> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.update;
        return this._http.put<Customer>(route,customer, this.prepareHeader());
    }

    addCustomer(customer : Customer): Observable<Customer> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.add;
        return this._http.post<Customer>(route,customer, this.prepareHeader());
    }

    addCustomers(customers : Customer[]): Observable<Customer[]> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.addRange;
        return this._http.post<Customer[]>(route,customers, this.prepareHeader());
    }

    GetAllCustomers(companyId: string): Observable<Customer[]> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.getAllCustomers + companyId;
        return this._http.get<Customer[]>(route, this.prepareHeader());
    }

    canAccessCustomer(companyId: string, customerId: string): Observable<boolean> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.canAccessCustomer + companyId + '/' + customerId;
        return this._http.get<boolean>(route, this.prepareHeader());
    }

    dossierSubmittedInformations(customerId: string): Observable<SubmittedDossier> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.dossierSubmittedInformations + customerId;
        return this._http.get<SubmittedDossier>(route, this.prepareHeader());
    }


    customerByDossierId(dossierId: number): Observable<Customer> {
        const route = this.apiRoute + environment.apiCatalog.customer.subRoutes.customerByDossierId + dossierId;
        return this._http.get<Customer>(route, this.prepareHeader());
    }
 
    private prepareHeader(): Object {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/json');
        return { headers: headers };
    }

    get sendAddress$(): Observable<any>
    {
        return this._sendAddress$.asObservable();
    }

    sendAddress(address: any): void
    {
        this._sendAddress$.next(address);
    }
}
